// Assemble le modèle du tableau de bord d'accueil : agrège les fonctions
// existantes (poids, calories, macros, insights), sans nouveau calcul métier.

import { aggregateBy } from './aggregate';
import { type BmiCategory, bmi, bmiCategory } from './bmi';
import { adherence, type DailyBudget, dailyBudget, dailyCalories } from './calorie-analytics';
import { todayKey } from './datetime';
import { waterStats } from './hydration';
import { consumptionNutrients, sumNutrients } from './macros';
import { buildInsights, type Insight } from './narrative';
import { resolveTargets } from './profile-targets';
import { mean, round } from './statistics';
import type { Consumption, Milestone, Profile, WaterEntry, WeightEntry } from './types';
import { type WeeklyDigest, weeklyDigest } from './weekly-digest';
import {
  currentWeight,
  isPlateau,
  milestoneProgress,
  progress,
  projectReachDate,
  smoothedWeight,
  weightTrend,
} from './weight-analytics';

export interface SummaryModel {
  hasWeight: boolean;
  hasConsumption: boolean;
  weight: {
    currentKg: number;
    deltaWeekKg: number;
    lostKg: number;
    trendPerWeek: number;
    toTargetKg: number;
    targetProgressPct: number;
    etaDays: number | null;
    bmiValue: number;
    bmiCategory: BmiCategory;
  };
  calories: {
    today: number;
    threshold: number;
    todayVs: number;
    avgKcal: number;
    adherencePct: number;
    currentStreak: number;
    bestStreak: number;
  };
  macros: {
    avgProtein: number;
    avgCarbs: number;
    avgFat: number;
    proteinTargetG: number;
    carbTargetG: number;
    fatTargetG: number;
  };
  nextMilestone: { label: string; targetWeightKg: number; remainingKg: number } | null;
  water: { today: number; targetMl: number; pct: number };
  budget: DailyBudget;
  weekly: WeeklyDigest;
  insights: Insight[];
  weightSpark: number[];
  calorieSpark: number[];
}

export interface SummaryInput {
  profile: Profile;
  weights: readonly WeightEntry[];
  consumptions: readonly Consumption[];
  milestones: readonly Milestone[];
  water: readonly WaterEntry[];
}

const SPARK_POINTS = 30;

export function buildSummary({
  profile,
  weights,
  consumptions,
  milestones,
  water,
}: SummaryInput): SummaryModel {
  const hasWeight = weights.length > 0;
  const hasConsumption = consumptions.length > 0;
  const threshold = profile.calorieThreshold;

  const prog = progress(weights, profile.targetWeightKg, profile.idealWeightKg);
  const trend = weightTrend(weights);
  const eta = projectReachDate(weights, profile.targetWeightKg);
  const sortedW = [...weights].sort((a, b) => a.date.localeCompare(b.date));
  const recent = sortedW.slice(-8);
  const deltaWeekKg =
    recent.length >= 2 ? round(currentWeight(weights) - recent[0]!.weightKg, 2) : 0;
  const bmiValue = hasWeight ? bmi(prog.currentKg, profile.heightCm) : NaN;
  const weightSpark = smoothedWeight(weights, 7)
    .slice(-SPARK_POINTS)
    .map((s) => s.smoothed);

  const phase = profile.trainingPhase;
  const daily = dailyCalories(consumptions, threshold, phase);
  const adh = adherence(consumptions, threshold, phase);
  const today = daily.find((d) => d.date === todayKey())?.kcal ?? 0;
  const calorieSpark = daily.slice(-SPARK_POINTS).map((d) => d.kcal);
  const budget = dailyBudget(today, threshold, phase);
  const weekly = weeklyDigest(consumptions, weights, threshold, profile.trainingPhase);

  // Macros : moyennes journalières.
  const dailyNut = aggregateBy(
    consumptions,
    (c) => c.datetime,
    'day',
    (g) => sumNutrients(g.map(consumptionNutrients)),
  ).map((b) => b.value);
  const avgProtein = dailyNut.length ? round(mean(dailyNut.map((n) => n.protein))) : 0;
  const avgCarbs = dailyNut.length ? round(mean(dailyNut.map((n) => n.carbs))) : 0;
  const avgFat = dailyNut.length ? round(mean(dailyNut.map((n) => n.fat))) : 0;

  // Objectifs effectifs (auto ↔ manuel).
  const targets = resolveTargets(profile, hasWeight ? prog.currentKg : NaN);

  const mp = milestoneProgress(weights, milestones);
  const next = mp.find((m) => !m.reached);
  const nextMilestone = next
    ? { label: next.label, targetWeightKg: next.targetWeightKg, remainingKg: next.remainingKg }
    : null;

  const wStats = waterStats(water, targets.waterMl);
  const waterPct = targets.waterMl > 0 ? Math.round((wStats.today / targets.waterMl) * 100) : 0;

  const insights = buildInsights({
    trendPerWeek: trend.slopePerWeek,
    trainingPhase: profile.trainingPhase,
    targetProgressPct: prog.targetProgressPct,
    toTargetKg: prog.toTargetKg,
    plateau: hasWeight && isPlateau(weights),
    etaDays: eta?.days ?? null,
    adherencePct: adh.adherencePct,
    currentStreak: adh.currentStreak,
    proteinAvgG: avgProtein,
    proteinTargetG: targets.proteinG,
    // Hydratation transmise seulement si suivie.
    ...(water.length > 0 ? { waterTodayMl: wStats.today, waterTargetMl: targets.waterMl } : {}),
  });

  return {
    hasWeight,
    hasConsumption,
    weight: {
      currentKg: prog.currentKg,
      deltaWeekKg,
      lostKg: prog.lostKg,
      trendPerWeek: round(trend.slopePerWeek, 2),
      toTargetKg: prog.toTargetKg,
      targetProgressPct: prog.targetProgressPct,
      etaDays: eta?.days ?? null,
      bmiValue,
      bmiCategory: bmiCategory(bmiValue),
    },
    calories: {
      today: Math.round(today),
      threshold,
      todayVs: round(today - threshold),
      avgKcal: adh.avgKcal,
      adherencePct: adh.adherencePct,
      currentStreak: adh.currentStreak,
      bestStreak: adh.bestStreak,
    },
    macros: {
      avgProtein,
      avgCarbs,
      avgFat,
      proteinTargetG: targets.proteinG,
      carbTargetG: targets.carbG,
      fatTargetG: targets.fatG,
    },
    nextMilestone,
    water: { today: Math.round(wStats.today), targetMl: targets.waterMl, pct: waterPct },
    budget,
    weekly,
    insights,
    weightSpark,
    calorieSpark,
  };
}
